import { API_URL } from "config";
import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
} from "@reduxjs/toolkit/query";
import { RootState } from "./store";
import { logout, tokenRefreshed } from "features/auth/authSlice";
import { showSessionExpired } from "features/notificationSlice";

const ACCESS_REFRESH_LEEWAY_SEC = 300;

let refreshInFlight: Promise<boolean> | null = null;

function getJwtExpMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4;
    const padded = pad ? payload + "=".repeat(4 - pad) : payload;
    const json = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof json.exp !== "number") return null;
    return json.exp * 1000;
  } catch {
    return null;
  }
}

function isAuthRefreshRequest(args: string | FetchArgs): boolean {
  if (typeof args === "string") return false;
  return args.url === "/auth/token/refresh/" && args.method === "POST";
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const { clientView } = state;
    if (clientView?.active && clientView.userId != null) {
      headers.set("X-View-As-User", String(clientView.userId));
    }
    return headers;
  },
});

async function tryRefreshTokens(
  api: BaseQueryApi,
  extraOptions: object,
): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;

    const refreshResult = await baseQuery(
      {
        url: `/auth/token/refresh/`,
        method: "POST",
        body: { refresh: refreshToken },
      },
      api,
      extraOptions,
    );

    if (!refreshResult.data) return false;

    const { access, refresh } = refreshResult.data as {
      access: string;
      refresh?: string;
    };
    localStorage.setItem("accessToken", access);
    const nextRefresh = refresh ?? refreshToken;
    if (refresh) localStorage.setItem("refreshToken", refresh);
    api.dispatch(tokenRefreshed({ access, refresh: nextRefresh }));
    return true;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const accessToken = state.auth.accessToken;

  if (
    !isAuthRefreshRequest(args) &&
    accessToken &&
    localStorage.getItem("refreshToken")
  ) {
    const expMs = getJwtExpMs(accessToken);
    if (expMs !== null) {
      const leewayMs = ACCESS_REFRESH_LEEWAY_SEC * 1000;
      if (Date.now() >= expMs - leewayMs) {
        const ok = await tryRefreshTokens(api, extraOptions);
        if (!ok) {
          api.dispatch(showSessionExpired());
          api.dispatch(logout());
          return {
            error: {
              status: 401,
              data: "Session expired",
            } as FetchBaseQueryError,
          };
        }
      }
    }
  }

  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (await tryRefreshTokens(api, extraOptions)) {
      if (typeof args != "string" && args.url == "/auth/token/verify/") {
        const token = (api.getState() as RootState).auth.accessToken;
        if (token) args.body = { token };
      }
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(showSessionExpired());
      api.dispatch(logout());
    }
  }
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Auth",
    "FileSystem",
    "AdminUsers",
    "AdminClients",
    "IntegrationSettings",
    "SftpConnections",
    "MajorJobs",
    "Partners",
    "SecureUploads",
    "SecureUploadsUnread",
  ],
  endpoints: () => ({}),
});

export default api;
